import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Loader2, Shield, Clock, Award, CheckCircle2 } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { useValuationStore } from '@/store/valuationStore'
import {
  AddressStep,
  PropertyStep,
  CharacteristicsStep,
  ServicesStep,
  ContactStep,
} from '@/components/forms'
import { calculateValuation, saveValuation } from '@/services/valuation.service'
import { saveLead, sendLeadNotification } from '@/services/lead.service'
import type { ValuationFormData } from '@/types'
import { CITY_NAME } from '@/config/supabase'

const steps = [
  { id: 1, name: 'Indirizzo', description: 'Posizione immobile', component: AddressStep },
  { id: 2, name: 'Proprietà', description: 'Tipo e superficie', component: PropertyStep },
  { id: 3, name: 'Caratteristiche', description: 'Piano e stato', component: CharacteristicsStep },
  { id: 4, name: 'Servizi', description: 'Extra e comfort', component: ServicesStep },
  { id: 5, name: 'Contatto', description: 'Ricevi valutazione', component: ContactStep },
]

export function Valuation() {
  const navigate = useNavigate()
  const {
    currentStep,
    formData,
    isLoading,
    error,
    nextStep,
    prevStep,
    isStepValid,
    setResult,
    setLoading,
    setError,
    resetForm,
  } = useValuationStore()

  useEffect(() => {
    resetForm()
    // Facebook Pixel: l'utente inizia la valutazione
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', { content_name: 'Valutazione Immobile' })
    }
  }, [resetForm])

  const CurrentStepComponent = steps[currentStep - 1]?.component

  const handleSubmit = async () => {
    if (!isStepValid(5)) return

    setLoading(true)
    setError(null)

    try {
      // 1. Calcola valutazione
      const result = await calculateValuation(formData as ValuationFormData)

      // 2-3. Persistenza non bloccante: se il database non risponde l'utente
      // riceve comunque la stima, calcolata in locale. Prima stavano nel try
      // esterno, quindi un errore di scrittura gli negava anche il risultato.
      try {
        const valuationId = await saveValuation(result)
        result.id = valuationId
        await saveLead(formData as ValuationFormData, valuationId)
      } catch (persistErr) {
        console.error('Salvataggio lead/valutazione fallito (non bloccante):', persistErr)
      }

      // 4. La notifica non dipende dal database, e quando il database e' giu'
      // e' l'unica copia del lead: parte comunque.
      sendLeadNotification(formData as ValuationFormData, result)

      // 4. Salva risultato e naviga (il pixel Lead si attiva su Results.tsx)
      setResult(result)
      navigate('/risultati')
    } catch (err) {
      console.error('Errore durante la valutazione:', err)
      setError('Si è verificato un errore. Riprova.')
    } finally {
      setLoading(false)
    }
  }

  const handleNext = () => {
    if (currentStep === 5) {
      handleSubmit()
    } else {
      // Facebook Pixel: l'utente avanza oltre lo step 3 (utente coinvolto)
      if (currentStep === 3 && typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', { content_name: 'Valutazione Step 4' })
      }
      nextStep()
    }
  }

  return (
    <div className="flex-1 bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - only on first step */}
      {currentStep === 1 && (
        <div className="bg-primary-600 text-white py-10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">
              Valuta il tuo immobile a {CITY_NAME}
            </h1>
            <p className="text-primary-100 text-lg mb-6">
              Ottieni una stima gratuita basata sui dati reali del compravenduto degli ultimi 12 mesi
            </p>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span>Dati reali compravenduto</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Risultato in 2 minuti</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                <span>100% gratuito</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <nav className="mb-8">
          <ol className="flex items-center justify-between">
            {steps.map((step, index) => (
              <li key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center w-full">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold transition-all ${
                      step.id < currentStep
                        ? 'bg-green-500 text-white'
                        : step.id === currentStep
                        ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step.id < currentStep ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs hidden sm:block text-center ${
                      step.id === currentStep
                        ? 'text-primary-600 font-semibold'
                        : step.id < currentStep
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded-full ${
                      step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>

        {/* Step info */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">
            Passo {currentStep} di {steps.length}: <span className="font-medium text-gray-700">{steps[currentStep - 1]?.description}</span>
          </p>
        </div>

        {/* Form Card */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-6 sm:p-8">
            {CurrentStepComponent && <CurrentStepComponent />}

            {error && (
              <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1 || isLoading}
                className="px-6"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Indietro
              </Button>

              <Button
                onClick={handleNext}
                disabled={!isStepValid(currentStep) || isLoading}
                className="px-6"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Calcolo in corso...
                  </>
                ) : currentStep === 5 ? (
                  <>
                    <Award className="h-4 w-4 mr-2" />
                    Ottieni valutazione
                  </>
                ) : (
                  <>
                    Avanti
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bottom trust text */}
        <p className="mt-6 text-center text-sm text-gray-500">
          🔒 I tuoi dati sono al sicuro e non saranno condivisi con terzi
        </p>
      </div>
    </div>
  )
}
